import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Dialog} from "primereact/dialog";
import {useTranslation} from "react-i18next";

import {DetailsPanelRow} from "../component/panels";
import {selectHasReferenceAddress} from "../redux/addressSlice";
import {
    selectNotifyAddressChangedFromOrderApi,
    selectNotifyAddressChangedFromOrderApiPreviousAddress,
    selectNotifyOrderAddressCouldNotBeAdopted,
    selectNotifySegmentChanged,
    selectOrderAddressCouldNotBeAdoptedFailedAddress,
    selectOrderAddressCouldNotBeAdoptedWasReferenceAddress,
    selectSegmentChangedPreviousSegment,
    selectSegmentChangedSource,
    setNotifyAddressChangedFromOrderApi,
    setNotifyOrderAddressCouldNotBeAdopted,
    setNotifySegmentChanged,
} from "../redux/notificationsSlice";
import {selectSegment} from "../redux/objectParametersSlice";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {selectMostRelevantOrderDetails} from "../redux/valueOrdersSelectors";

function OrderAddressCard() {
    const {t} = useTranslation();
    const orderDetails = useRevaSelector(selectMostRelevantOrderDetails);
    const addr = orderDetails?.addressInformation;

    if (!addr) return null;

    const streetHouseNr = addr.houseNumber ? `${addr.street} ${addr.houseNumber}` : addr.street;
    const orderAddressQuery = `${streetHouseNr}, ${addr.zipCode} ${addr.city}`;

    return (
        <Card className="InfoCard bg-surface-100 dark:bg-surface-700">
            <div className="font-bold text-lg mb-4 pb-2 border-b border-gray-400">
                <span className="pi pi-building mr-2" />
                {orderAddressQuery}
            </div>
            <DetailsPanelRow label={t("order.object.streetHouseNr")} value={streetHouseNr} />
            <DetailsPanelRow label={t("order.object.addressAddition")} value={addr?.addressAddition} />
            <DetailsPanelRow label={t("order.object.zipCode")} value={addr?.zipCode} />
            <DetailsPanelRow label={t("order.object.city")} value={addr?.city} />
            <DetailsPanelRow label={t("order.object.district")} value={addr?.district} />
            <DetailsPanelRow label={t("order.object.state")} value={addr?.state} />
            <DetailsPanelRow label={t("order.object.referenceAddress")} value={addr?.referenceAddress} />
        </Card>
    );
}

function NotificationAddressChangedFromOrdersApi() {
    const dispatch = useRevaDispatch();
    const notificationVisible = useRevaSelector(selectNotifyAddressChangedFromOrderApi);
    const previousAddress = useRevaSelector(selectNotifyAddressChangedFromOrderApiPreviousAddress);
    const {t} = useTranslation();

    const onHideNotification = () => {
        dispatch(setNotifyAddressChangedFromOrderApi({enabled: false}));
    };

    const footerContent = (
        <div>
            <Button
                label={t("general.close")}
                icon="pi pi-times"
                severity="secondary"
                text
                onClick={onHideNotification}
            />
        </div>
    );

    return (
        <Dialog
            header={t("notifications.addressChangedFromOrdersApi.title")}
            visible={notificationVisible}
            onHide={onHideNotification}
            closeOnEscape={true}
            resizable={false}
            footer={footerContent}
            style={{maxWidth: "50rem"}}
        >
            <p className="mb-4 mt-0">{t("notifications.addressChangedFromOrdersApi.notificationText")}</p>
            <p className="mb-4 mt-0">
                {t("notifications.addressChangedFromOrdersApi.previousAddress")}
                <br />
                <strong>{previousAddress}</strong>
            </p>
            <p className="mb-4 mt-0">{t("notifications.addressChangedFromOrdersApi.newAddress")}</p>
            <OrderAddressCard />
        </Dialog>
    );
}

function NotificationOrderAddressCouldNotBeAdopted() {
    const dispatch = useRevaDispatch();
    const notificationVisible = useRevaSelector(selectNotifyOrderAddressCouldNotBeAdopted);
    const failedAddress = useRevaSelector(selectOrderAddressCouldNotBeAdoptedFailedAddress);
    const wasReferenceAddress = useRevaSelector(selectOrderAddressCouldNotBeAdoptedWasReferenceAddress);
    const valuationHasReferenceAddress = useRevaSelector(selectHasReferenceAddress);
    const {t} = useTranslation();

    // i18next context suffix for the value order's failed address (title/text/hint): address vs. reference address.
    const context = wasReferenceAddress ? "reference" : undefined;
    // i18next context suffix for the "kept" hint: depends on what THIS valuation currently has, not on the
    // value order — a valuation with a reference address keeps both address and reference address.
    const keptContext = valuationHasReferenceAddress ? "reference" : undefined;

    const onHideNotification = () => {
        dispatch(setNotifyOrderAddressCouldNotBeAdopted({enabled: false}));
    };

    const footerContent = (
        <div>
            <Button
                label={t("general.close")}
                icon="pi pi-times"
                severity="secondary"
                text
                onClick={onHideNotification}
            />
        </div>
    );

    return (
        <Dialog
            header={t("notifications.addressCouldNotBeAdopted.title", {context})}
            visible={notificationVisible}
            onHide={onHideNotification}
            closeOnEscape={true}
            resizable={false}
            footer={footerContent}
            style={{maxWidth: "50rem"}}
        >
            <p className="mb-4 mt-0">{t("notifications.addressCouldNotBeAdopted.notificationText", {context})}</p>
            <p className="mb-4 mt-0">
                <strong>{failedAddress}</strong>
            </p>
            <p className="mb-4 mt-0">
                {t("notifications.addressCouldNotBeAdopted.keptAddress", {context: keptContext})}
            </p>
            <p className="mb-0 mt-0">{t("notifications.addressCouldNotBeAdopted.correctionHint", {context})}</p>
        </Dialog>
    );
}

/**
 * #19444: Eine externe Quelle hat das Segment verschoben und dabei die im Überblick geänderten Werte
 * verworfen. Ohne Meldung wirken sie grundlos verschwunden. Besichtigung und Exposé bekommen
 * getrennte Texte: im einen Fall hat jemand anderes parallel etwas ausgelöst, im anderen war es der
 * eigene Import, der gerade lief.
 */
function NotificationSegmentChanged() {
    const dispatch = useRevaDispatch();
    const notificationVisible = useRevaSelector(selectNotifySegmentChanged);
    const previousSegment = useRevaSelector(selectSegmentChangedPreviousSegment);
    const source = useRevaSelector(selectSegmentChangedSource);
    const currentSegment = useRevaSelector(selectSegment);
    const {t} = useTranslation();

    const context = source === "IMMODROP" ? "immodrop" : "inspection";

    const onHideNotification = () => {
        dispatch(setNotifySegmentChanged({enabled: false}));
    };

    const footerContent = (
        <div>
            <Button
                label={t("general.close")}
                icon="pi pi-times"
                severity="secondary"
                text
                onClick={onHideNotification}
            />
        </div>
    );

    return (
        <Dialog
            header={t("notifications.segmentChanged.title", {context})}
            visible={notificationVisible}
            onHide={onHideNotification}
            closeOnEscape={true}
            resizable={false}
            footer={footerContent}
            style={{maxWidth: "50rem"}}
        >
            <p className="mb-4 mt-0">{t("notifications.segmentChanged.notificationText", {context})}</p>
            <p className="mb-4 mt-0">
                {t("notifications.segmentChanged.previousSegment")}
                <br />
                <strong>{previousSegment ? t(`object.segments.${previousSegment}`) : "-"}</strong>
            </p>
            <p className="mb-4 mt-0">
                {t("notifications.segmentChanged.newSegment")}
                <br />
                <strong>{currentSegment ? t(`object.segments.${currentSegment}`) : "-"}</strong>
            </p>
            <p className="mb-0 mt-0">{t("notifications.segmentChanged.overridesDiscarded")}</p>
        </Dialog>
    );
}

export function GlobalNotifications() {
    return (
        <>
            <NotificationAddressChangedFromOrdersApi />
            <NotificationOrderAddressCouldNotBeAdopted />
            <NotificationSegmentChanged />
        </>
    );
}
